// useSilentMutationLogger — Observerar workflow-mutations via count-diff
// och fire:ar redacted action-log-entries.
//
// Opt-in wrapper runt `useActionLog.logAction`. Call-sites behöver inte
// ändras — hook:en sitter på toppen av Index.tsx och diff:ar blocks/
// connectors/groups-arrays för att detektera create/delete.

import { useEffect, useRef } from 'react';
import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import type { LogActionInput } from '@/hooks/useActionLog';

export interface SilentLoggerInput {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  logAction: (input: LogActionInput) => void;
  /** Om false: hook:en tystas helt (t.ex. viewOnly mode). */
  enabled?: boolean;
}

export function useSilentMutationLogger({
  blocks, connectors, groups, logAction, enabled = true,
}: SilentLoggerInput) {
  const prevBlocksRef = useRef(new Map<string, WorkflowBlock>());
  const prevConnectorsRef = useRef(new Map<string, Connector>());
  const prevGroupsRef = useRef(new Map<string, BlockGroup>());

  useEffect(() => {
    if (!enabled) return;
    const prev = prevBlocksRef.current;
    const next = new Map(blocks.map(b => [b.id, b]));

    // Created
    for (const [id] of next) {
      if (!prev.has(id)) {
        logAction({
          action: 'Block created',
          kind: 'canvas.block.create',
          payload: { blockId: id },
        });
      }
    }
    // Deleted
    for (const [id] of prev) {
      if (!next.has(id)) {
        logAction({
          action: 'Block deleted',
          kind: 'canvas.block.delete',
          payload: { blockId: id },
        });
      }
    }
    // Moved — bara logga x/y-diff, ingen user-content
    for (const [id, b] of next) {
      const p = prev.get(id);
      if (p && (p.x !== b.x || p.y !== b.y)) {
        logAction({
          action: 'Block moved',
          kind: 'canvas.block.move',
          payload: { blockId: id, x: b.x, y: b.y },
        });
      }
    }

    prevBlocksRef.current = next;
  }, [blocks, logAction, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const prev = prevConnectorsRef.current;
    const next = new Map(connectors.map(c => [c.id, c]));

    for (const [id, c] of next) {
      if (!prev.has(id)) {
        logAction({
          action: 'Connector created',
          kind: 'canvas.connector.create',
          payload: { connectorId: id, fromId: c.fromId, toId: c.toId },
        });
      }
    }
    for (const [id] of prev) {
      if (!next.has(id)) {
        logAction({
          action: 'Connector deleted',
          kind: 'canvas.connector.delete',
          payload: { connectorId: id },
        });
      }
    }

    prevConnectorsRef.current = next;
  }, [connectors, logAction, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const prev = prevGroupsRef.current;
    const next = new Map(groups.map(g => [g.id, g]));

    for (const [id, g] of next) {
      if (!prev.has(id)) {
        logAction({
          action: g.collapsed ? 'Layer created' : 'Group created',
          kind: 'canvas.group.create',
          payload: { groupId: id },
        });
      } else {
        const p = prev.get(id)!;
        if (p.collapsed !== g.collapsed) {
          logAction({
            action: g.collapsed ? 'Group collapsed' : 'Group expanded',
            kind: g.collapsed ? 'canvas.group.collapse' : 'canvas.group.expand',
            payload: { groupId: id },
          });
        }
      }
    }
    for (const [id] of prev) {
      if (!next.has(id)) {
        logAction({
          action: 'Group deleted',
          kind: 'canvas.group.delete',
          payload: { groupId: id },
        });
      }
    }

    prevGroupsRef.current = next;
  }, [groups, logAction, enabled]);
}
